import type { ParkingLayoutCanvas, ParkingLayoutGateDirection, ParkingLayoutObjectDefinition, ParkingLayoutRecord } from "./parkingTypes";
import { getParkingLayouts } from "./parkingDataSource";

export type ParkingLayoutCellBase = {
  key: string;
  row: number;
  column: number;
};

export type ParkingLayoutSpotCell = ParkingLayoutCellBase & {
  type: "spot";
  id: string;
  label: string;
  occupied: boolean;
  facing?: ParkingLayoutGateDirection;
};

export type ParkingLayoutGateCell = ParkingLayoutCellBase & {
  type: "entrance" | "exit";
  direction: ParkingLayoutGateDirection;
  label?: string;
};

export type ParkingLayoutRoadCell = ParkingLayoutCellBase & {
  type: "road";
};

export type ParkingLayoutVoidCell = ParkingLayoutCellBase & {
  type: "void";
};

export type ParkingLayoutCell =
  | ParkingLayoutSpotCell
  | ParkingLayoutGateCell
  | ParkingLayoutRoadCell
  | ParkingLayoutVoidCell;

export type ParkingLayoutSummary = {
  total: number;
  occupied: number;
  free: number;
};

export type ParkingLayoutGridRenderData = {
  mode: "grid";
  rows: number;
  columns: number;
  cells: ParkingLayoutCell[];
  spots: ParkingLayoutSpotCell[];
  summary: ParkingLayoutSummary;
  layoutRecord: ParkingLayoutRecord;
};

export type ParkingLayoutRenderableObject = ParkingLayoutObjectDefinition & {
  occupied?: boolean;
  label?: string;
};

export type ParkingLayoutObjectsRenderData = {
  mode: "objects";
  canvas: ParkingLayoutCanvas;
  objects: ParkingLayoutRenderableObject[];
  summary: ParkingLayoutSummary;
  layoutRecord: ParkingLayoutRecord;
};

export type ParkingLayoutRenderData = ParkingLayoutGridRenderData | ParkingLayoutObjectsRenderData;

const parkingLayouts = getParkingLayouts();

const keyFor = (row: number, column: number) => `${row}:${column}`;

export function getParkingLayoutRecord(cityId: string, spotId: string): ParkingLayoutRecord | undefined {
  const normalizedCity = cityId.toLowerCase();
  const normalizedSpot = spotId.toLowerCase();

  return parkingLayouts.layouts.find(
    (entry) => entry.cityId?.toLowerCase() === normalizedCity && entry.spotId?.toLowerCase() === normalizedSpot,
  );
}

const clampRange = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const resolveOccupiedSet = (ids: string[], desiredCount: number, explicit: string[] = []) => {
  const occupiedSet = new Set<string>();

  for (const slotId of explicit) {
    if (occupiedSet.size >= desiredCount) {
      break;
    }
    occupiedSet.add(slotId);
  }

  if (occupiedSet.size < desiredCount) {
    for (const slotId of ids) {
      if (occupiedSet.size >= desiredCount) {
        break;
      }
      if (!occupiedSet.has(slotId)) {
        occupiedSet.add(slotId);
      }
    }
  }

  if (occupiedSet.size > desiredCount) {
    const trimmed = Array.from(occupiedSet).slice(0, desiredCount);
    occupiedSet.clear();
    for (const slotId of trimmed) {
      occupiedSet.add(slotId);
    }
  }

  return occupiedSet;
};

const defaultCanvasForObjects = (objects: ParkingLayoutObjectDefinition[]): ParkingLayoutCanvas => {
  if (objects.length === 0) {
    return { width: 50, height: 50, gridSize: 1 };
  }

  const padding = 4;
  let maxX = 0;
  let maxY = 0;

  for (const object of objects) {
    const endX = object.x + object.width;
    const endY = object.y + object.height;
    maxX = Math.max(maxX, endX);
    maxY = Math.max(maxY, endY);
  }

  return {
    width: Math.ceil(maxX + padding),
    height: Math.ceil(maxY + padding),
    gridSize: 1,
  };
};

const buildObjectsRenderData = (
  layout: ParkingLayoutRecord,
  occupancy: { total: number; occupied: number },
): ParkingLayoutObjectsRenderData => {
  const objects = layout.objects ?? [];
  const canvas = layout.canvas ?? defaultCanvasForObjects(objects);

  const spotObjects = objects.filter((object) => object.type === "spot");
  const spotIds = spotObjects.map((object) => object.id);
  const desiredOccupiedCount = Math.max(0, Math.min(occupancy.occupied, spotIds.length));
  const explicitOccupied = (layout.occupiedSlots ?? []).filter((slotId) => spotIds.includes(slotId));
  const occupiedSet = resolveOccupiedSet(spotIds, desiredOccupiedCount, explicitOccupied);

  const renderObjects: ParkingLayoutRenderableObject[] = objects.map((object) => {
    if (object.type !== "spot") {
      return { ...object };
    }

    const meta = object.meta ?? {};
    const label = typeof meta.label === "string" && meta.label.length > 0 ? meta.label : object.id;
    const occupied = occupiedSet.has(object.id);

    return {
      ...object,
      label,
      occupied,
    };
  });

  const occupiedCount = renderObjects.filter((object) => object.type === "spot" && object.occupied).length;
  const totalSpots = renderObjects.filter((object) => object.type === "spot").length;

  return {
    mode: "objects",
    canvas,
    objects: renderObjects,
    summary: {
      total: totalSpots,
      occupied: occupiedCount,
      free: Math.max(totalSpots - occupiedCount, 0),
    },
    layoutRecord: layout,
  };
};

const buildGridRenderData = (
  layout: ParkingLayoutRecord,
  occupancy: { total: number; occupied: number },
): ParkingLayoutGridRenderData => {
  const { grid, spots = [], entrance, exit, roads } = layout;

  if (!grid) {
    throw new Error("Grid layout definition is missing grid dimensions.");
  }

  const rows = Math.max(grid.rows, 0);
  const columns = Math.max(grid.columns, 0);
  const cellsMap = new Map<string, ParkingLayoutCell>();

  const applyRoad = (row: number, column: number) => {
    if (row < 0 || column < 0 || row >= rows || column >= columns) {
      return;
    }
    const key = keyFor(row, column);
    const existing = cellsMap.get(key);
    if (existing && existing.type !== "void") {
      return;
    }
    cellsMap.set(key, { key, row, column, type: "road" });
  };

  for (const segment of roads ?? []) {
    const startRow = clampRange(Math.min(segment.from.row, segment.to.row), 0, rows - 1);
    const endRow = clampRange(Math.max(segment.from.row, segment.to.row), 0, rows - 1);
    const startColumn = clampRange(Math.min(segment.from.column, segment.to.column), 0, columns - 1);
    const endColumn = clampRange(Math.max(segment.from.column, segment.to.column), 0, columns - 1);

    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
      for (let columnIndex = startColumn; columnIndex <= endColumn; columnIndex += 1) {
        applyRoad(rowIndex, columnIndex);
      }
    }
  }

  const order = spots.map((spot) => spot.id);
  const desiredOccupiedCount = Math.max(0, Math.min(occupancy.occupied, order.length));
  const explicitOccupied = (layout.occupiedSlots ?? []).filter((slotId) => order.includes(slotId));
  const occupiedSet = resolveOccupiedSet(order, desiredOccupiedCount, explicitOccupied);

  const spotCells: ParkingLayoutSpotCell[] = spots.map((spot) => {
    const row = clampRange(spot.row, 0, rows - 1);
    const column = clampRange(spot.column, 0, columns - 1);
    const key = keyFor(row, column);
    const occupied = occupiedSet.has(spot.id);

    const cell: ParkingLayoutSpotCell = {
      key,
      row,
      column,
      type: "spot",
      id: spot.id,
      label: spot.label,
      facing: spot.facing,
      occupied,
    };

    cellsMap.set(key, cell);
    return cell;
  });

  const placeGate = (kind: "entrance" | "exit", gate?: { row: number; column: number; direction: ParkingLayoutGateDirection; label?: string }) => {
    if (!gate) {
      return;
    }
    const row = clampRange(gate.row, 0, rows - 1);
    const column = clampRange(gate.column, 0, columns - 1);
    const key = keyFor(row, column);
    cellsMap.set(key, {
      key,
      row,
      column,
      type: kind,
      direction: gate.direction,
      label: gate.label,
    });
  };

  placeGate("entrance", entrance);
  placeGate("exit", exit);

  for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
      const key = keyFor(rowIndex, columnIndex);
      if (!cellsMap.has(key)) {
        cellsMap.set(key, { key, row: rowIndex, column: columnIndex, type: "void" });
      }
    }
  }

  const cells = Array.from(cellsMap.values()).sort((a, b) => {
    if (a.row === b.row) {
      return a.column - b.column;
    }
    return a.row - b.row;
  });

  const occupiedCount = spotCells.filter((cell) => cell.occupied).length;
  const totalSpots = spotCells.length;

  return {
    mode: "grid",
    rows,
    columns,
    cells,
    spots: spotCells,
    summary: {
      total: totalSpots,
      occupied: occupiedCount,
      free: Math.max(totalSpots - occupiedCount, 0),
    },
    layoutRecord: layout,
  };
};

export function buildParkingLayoutRenderData(
  layout: ParkingLayoutRecord,
  occupancy: { total: number; occupied: number },
): ParkingLayoutRenderData {
  if (layout.objects && layout.objects.length > 0) {
    return buildObjectsRenderData(layout, occupancy);
  }

  if (layout.grid && layout.spots && layout.spots.length > 0) {
    return buildGridRenderData(layout, occupancy);
  }

  return {
    mode: "objects",
    canvas: { width: 50, height: 50, gridSize: 1 },
    objects: [],
    summary: {
      total: 0,
      occupied: 0,
      free: 0,
    },
    layoutRecord: layout,
  };
}
